/**
 * Capture-form logic, kept out of the component so it can be tested without a DOM.
 *
 * The two decisions here both come from the Phase 0 prototype, and both are about a *wrong guess*
 * being worse than no guess:
 *
 * **Is this a link or a name?** The form has one leading input because most saves start with a pasted
 * link, and asking for the link and then the name doubles the work for the common case. But a name
 * misread as a link leaves the place with no name at all — so the test requires a scheme *and* a
 * dotted host, and `cafe.com` typed as a name stays a name.
 *
 * **What is a name for this link?** A guess that is visibly wrong is fine because the user edits it;
 * a guess that is silently empty is not, because the field is required and they get no starting point.
 */

import { PlaceCategory } from "@trippool/contracts";
import { classifyLocationInput } from "@trippool/core";

import { distanceMetres } from "./geo.ts";

/**
 * The URL if this input is one, or null if it is a name.
 *
 * Requires an `http`/`https` scheme and a dotted hostname. The scheme requirement is what keeps
 * `cafe.com` — which a user might type as a name — out of the URL branch.
 */
export function asUrl(value: string): string | null {
  const trimmed = value.trim();
  // A space is disqualifying: a URL has none, and a name usually does.
  if (trimmed === "" || /\s/.test(trimmed)) return null;
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
    return parsed.hostname.includes(".") ? trimmed : null;
  } catch {
    return null;
  }
}

/**
 * A first guess at a name from a link.
 *
 * A social post URL usually carries the creator's handle, which is a far better guess than the
 * hostname: `tiktok.com/@theflorestdalat/video/…` should suggest `theflorestdalat`, not `Tiktok`.
 * Falls back to the hostname with `www.` stripped, because a bare host is at least a starting point
 * the user can edit.
 *
 * Returns an empty string rather than throwing, so the caller can leave the field blank and let the
 * required-field check speak.
 */
export function nameFromUrl(url: string): string {
  try {
    const parsed = new URL(url);
    const handle = parsed.pathname.match(/@([^/]+)/);
    const fromHandle = handle?.[1];
    if (fromHandle !== undefined && fromHandle !== "") {
      return fromHandle.replace(/[._-]+/g, " ").trim();
    }
    const host = parsed.hostname.replace(/^www\./, "").split(".")[0] ?? "";
    return host === "" ? "" : host.charAt(0).toUpperCase() + host.slice(1);
  } catch {
    return "";
  }
}

/**
 * Diacritic-insensitive folding, for comparing two names the user typed.
 *
 * This exists for *display only* — to avoid offering a suggestion the user has already added, and to
 * keep one row in a tag list while they are still typing it. It is deliberately not the rule that
 * decides anything: the database's `fold_vietnamese` is, and `POST /areas` is idempotent on it. A
 * disagreement between this and the database can at worst show a duplicate suggestion for a moment;
 * it can never store two areas, because the server collapses them.
 *
 * Copied rather than called because the database function is not reachable from a browser, and the
 * costs of the two copies are asymmetric: this one is cosmetic, the server's is authoritative.
 */
export function foldForCompare(input: string): string {
  return input
    .normalize("NFD")
    // The combining marks Vietnamese uses, matching the database function's list. Missing one here
    // means a suggestion appears twice, not that data is wrong.
    .replace(/[\u0300\u0301\u0302\u0303\u0306\u0309\u031b\u0323]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "d")
    .toLowerCase()
    .trim();
}

/**
 * Whether a set of best-time slots is one the API will accept.
 *
 * `cả ngày` is exclusive, enforced by a database CHECK. The client must not let a user build the
 * combination at all, because the alternative is a 400 for something the form offered — and the form
 * is the only place that knows the two checkboxes contradict each other.
 */
export function isBestTimeSelectionValid(slots: readonly string[]): boolean {
  if (slots.includes("cả ngày")) return slots.length === 1;
  return new Set(slots).size === slots.length;
}

/**
 * Apply a checkbox change to best-time slots, keeping the selection valid.
 *
 * Selecting `cả ngày` clears the others and selecting any other clears `cả ngày` — the meaning of
 * "suits the whole day" is that naming a specific slot as well adds nothing. Returns the next
 * selection rather than the component deciding, so the rule is testable.
 */
export function toggleBestTime(slots: readonly string[], slot: string): string[] {
  const has = slots.includes(slot);
  if (has) return slots.filter((existing) => existing !== slot);

  if (slot === "cả ngày") return ["cả ngày"];
  return [...slots.filter((existing) => existing !== "cả ngày"), slot];
}

/**
 * Whether a provider result is worth offering, and what to say when it is not.
 *
 * Measured: `Nook Cafe` in Đà Lạt exists in Google Maps and nowhere in OSM, and the provider answers
 * with the city 4 km off. A plausible-looking wrong coordinate is worse than none — at 1 200 m the
 * plan's order changes and at 2 500 m the route is 7.5 km worse — so a result the provider did not
 * really recognise must not look like one it did.
 *
 * The thresholds are `matchQuality` as the provider adapter computes it, on folded names.
 */
export function matchConfidence(matchQuality: number): "strong" | "weak" | "unusable" {
  if (matchQuality >= 0.9) return "strong";
  if (matchQuality >= 0.5) return "weak";
  return "unusable";
}

/**
 * What the capture form starts with, when another screen handed it something.
 *
 * It lives here, as a pure function of the query string, because the defect it fixes was invisible to
 * every test the app had. Home navigated to `/capture?q=<what was pasted>` and the form read no `q`, so
 * the user pasted a link, pressed Lưu, and arrived at an **empty** form: measured in the browser, the URL
 * carried the link while `.field__input--lead` held `""`. The only test covering that flow parsed a URL
 * it had built itself, so it passed for as long as the screen ignored the parameter.
 *
 * `name` and the coordinate fields are the map-search handoff: adopting a provider result has to carry
 * the provider's own fields, or the saved place records nothing about where its coordinate came from and
 * cannot be deduplicated by identity later — which is what `find_place_by_provider` reads.
 */
export interface CapturePrefill {
  /** The pasted or typed text. A link or a name; the leading field takes either. */
  text: string;
  /** A place name from the map search. Empty when the handoff was only text. */
  name: string;
  address: string;
  /** The provider's category, already checked against the contract's vocabulary. */
  category: PlaceCategory | null;
  /** Which upstream answered, for `place_sources.provider`. */
  provider: string | null;
  /** The provider's own identity for the place, for `place_sources.provider_place_id`. */
  providerPlaceId: string | null;
  /** Present only when both coordinates were there and parsed as finite numbers. */
  location: { lat: number; lon: number } | null;
}

export function capturePrefill(params: URLSearchParams): CapturePrefill {
  const category = params.get("category");
  return {
    text: params.get("q") ?? "",
    name: params.get("name") ?? "",
    address: params.get("address") ?? "",
    category:
      category !== null && (PlaceCategory.options as readonly string[]).includes(category)
        ? (category as PlaceCategory)
        : null,
    location: coordinateFrom(params),
    provider: params.get("provider"),
    providerPlaceId: params.get("provider_place_id"),
  };
}

/**
 * Both coordinates, or none.
 *
 * `Number("")` is 0 and `Number(null)` is 0, so a missing parameter would otherwise become a coordinate
 * in the Gulf of Guinea — and a wrong coordinate is invisible where a missing one is not, which is the
 * rule this whole screen is built around. Checked on the raw strings rather than on the numbers, because
 * by then the absence and a real zero are the same value.
 */
function coordinateFrom(params: URLSearchParams): { lat: number; lon: number } | null {
  const lat = params.get("lat");
  const lon = params.get("lon");
  if (lat === null || lon === null) return null;
  const parsedLat = Number(lat);
  const parsedLon = Number(lon);
  if (!Number.isFinite(parsedLat) || !Number.isFinite(parsedLon)) return null;
  return { lat: parsedLat, lon: parsedLon };
}

/**
 * What to do with text a user pasted into a location field.
 *
 * The decision is `classifyLocationInput` in `@trippool/core` — pure, and shared with the server so the
 * two cannot disagree about what a URL means — applied to the two questions this screen actually has:
 * whether anything can be adopted without a request, and whether the server is needed.
 *
 * **The local cases are the point, not an optimisation.** A full Google Maps URL and a bare coordinate
 * pair both carry the answer in the string, so they are adopted with no request at all: instant, and it
 * works with the network down. Only a `maps.app.goo.gl` share link needs the server, because a browser
 * cannot read a cross-origin `Location` header.
 *
 * Returning a description rather than performing the adoption keeps the decision testable without a DOM
 * and keeps the network call in the component that owns the form's state — the split this file already
 * uses for `capturePrefill`.
 */
export type LocationPlan =
  /** Adopt this coordinate now. `name` is worth prefilling when the field for it is empty. */
  | { action: "adopt"; location: { lat: number; lon: number }; name: string | null }
  /** Ask the server to expand this short link. */
  | { action: "resolve"; url: string }
  /** A Google Maps URL that carries no coordinate. Distinct from "nothing found", and the user can act on it. */
  | { action: "no-coordinate" }
  /** Not a location at all: hand it to the geocoder as a name. */
  | { action: "search" };

export function planForLocationInput(text: string): LocationPlan {
  const classified = classifyLocationInput(text);
  switch (classified.kind) {
    case "coordinates":
      return { action: "adopt", location: classified.location, name: null };
    case "maps-url":
      return { action: "adopt", location: classified.location, name: classified.name };
    case "maps-short-link":
      return { action: "resolve", url: classified.url };
    case "maps-url-without-coordinate":
      return { action: "no-coordinate" };
    case "text":
      return { action: "search" };
  }
}

/** The least a place needs for its area to be derived from a coordinate: where it is, and its areas. */
export interface SavedPlaceWithAreas {
  location: { lat: number; lon: number } | null;
  areas: Array<{ id: string; name: string }>;
}

/**
 * How far a saved place may be for its area to count as this coordinate's.
 *
 * A derivation, not a claim: 5 km is an area-sized neighbourhood in a provincial city (Đà Lạt is about
 * 10 km across), so a pin anywhere in town finds the area a nearby saved place carries, and a pin in
 * another province finds nothing rather than borrowing an unrelated label.
 */
export const NEAREST_AREA_RADIUS_M = 5_000;

/**
 * The areas of the nearest saved place to a coordinate, or `null`.
 *
 * This is the "nearest saved area" derivation, and its whole point is the `null`: `null` means no saved
 * place is close enough to say, and the form then leaves the field empty rather than filling it with a
 * plausible guess. It reads a coordinate and saved places only — no provider, no request — and it never
 * invents an area: the pooled place's own areas are what come back, so the value is the user's own
 * vocabulary, not a name this function made up.
 */
export function nearestSavedAreas(
  places: readonly SavedPlaceWithAreas[],
  point: { lat: number; lon: number },
  radiusM: number = NEAREST_AREA_RADIUS_M,
): Array<{ id: string; name: string }> | null {
  let best: Array<{ id: string; name: string }> | null = null;
  let bestDistance = radiusM;
  for (const place of places) {
    // A place with no coordinate cannot be measured to, and one with no areas has nothing to lend.
    if (place.location === null || place.areas.length === 0) continue;
    const distance = distanceMetres(point, place.location);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = place.areas;
    }
  }
  return best;
}
